import { target, targetable } from '@github/catalyst/lib/targetable'
import { cropCanvasAndExportToPNG } from '../submission_form/crop_canvas'

const SIGNATURE_FONTS = ['Alex Brush', 'Satisfy', 'Mr De Haviland', 'Yellowtail']

const FONT_FACE_CSS = `
@font-face {
  font-family: 'Alex Brush';
  font-style: normal;
  font-weight: 400;
  src: url(/fonts/AlexBrush.woff2) format('woff2');
}
@font-face {
  font-family: 'Satisfy';
  font-style: normal;
  font-weight: 400;
  src: url(/fonts/Satisfy.woff2) format('woff2');
}
@font-face {
  font-family: 'Mr De Haviland';
  font-style: normal;
  font-weight: 400;
  src: url(/fonts/MrDeHaviland.woff2) format('woff2');
}
@font-face {
  font-family: 'Yellowtail';
  font-style: normal;
  font-weight: 400;
  src: url(/fonts/Yellowtail.woff2) format('woff2');
}
`

let fontsStyleAdded = false

export default targetable(class extends HTMLElement {
  static [target.static] = ['canvas', 'input', 'clear', 'button', 'textInput', 'fontSelect']

  async connectedCallback () {
    this.scale = 3
    this.isTypeMode = this.dataset.mode === 'type'
    this.currentFont = 'Alex Brush'
    this.fontsLoaded = false

    // Set explicit canvas dimensions
    const containerWidth = this.canvas.parentNode.clientWidth || 400
    const canvasHeight = 80

    this.canvas.width = containerWidth * this.scale
    this.canvas.height = canvasHeight * this.scale
    this.canvas.style.height = canvasHeight + 'px'
    this.canvas.style.width = '100%'

    if (this.isTypeMode) {
      await this.initTypeMode()
    } else {
      await this.initDrawMode()
    }

    if (this.clear) {
      this.clear.addEventListener('click', (e) => {
        e.preventDefault()
        this.clearCanvas()
      })
    }

    this.button.addEventListener('click', (e) => {
      e.preventDefault()
      this.button.disabled = true
      this.submit()
    })
  }

  async initDrawMode () {
    this.canvas.getContext('2d').scale(this.scale, this.scale)
    const { default: SignaturePad } = await import('signature_pad')
    this.pad = new SignaturePad(this.canvas)
  }

  async initTypeMode () {
    await this.loadAllFonts()

    if (this.textInput) {
      this.textInput.addEventListener('input', (e) => {
        this.updateTypedSignature(e.target.value)
      })
      this.textInput.focus()
    }

    if (this.fontSelect) {
      this.currentFont = this.fontSelect.value || 'Alex Brush'
      this.fontSelect.addEventListener('change', (e) => {
        this.currentFont = e.target.value
        if (this.textInput.value) {
          this.updateTypedSignature(this.textInput.value)
        }
      })
    }
  }

  async loadAllFonts () {
    if (!fontsStyleAdded) {
      const style = document.createElement('style')
      style.textContent = FONT_FACE_CSS
      document.head.appendChild(style)
      fontsStyleAdded = true
      console.log('Signature fonts style added to document')
    }

    // Wait for fonts to be ready
    try {
      await Promise.all(SIGNATURE_FONTS.map(font => document.fonts.load(`48px "${font}"`)))
      console.log('Fonts loaded:', SIGNATURE_FONTS)
    } catch (e) {
      console.warn('Font loading error:', e)
    }
    await document.fonts.ready
    this.fontsLoaded = true
    console.log('All fonts ready, available fonts:', [...document.fonts].map(f => f.family))
  }

  updateTypedSignature (text) {
    const canvas = this.canvas
    const ctx = canvas.getContext('2d')

    // Clear the entire canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (!text) return

    const fontFamily = this.currentFont
    let fontSize = 50 * this.scale

    console.log('Drawing with font:', fontFamily, 'fontsLoaded:', this.fontsLoaded)

    // Set font and measure
    ctx.font = `${fontSize}px "${fontFamily}"`
    ctx.fillStyle = '#000000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Adjust font size to fit
    const maxWidth = canvas.width - 40
    while (ctx.measureText(text).width > maxWidth && fontSize > 12) {
      fontSize -= 2
      ctx.font = `${fontSize}px "${fontFamily}"`
    }

    // Draw text centered
    ctx.fillText(text, canvas.width / 2, canvas.height / 2)
  }

  clearCanvas () {
    if (this.pad) {
      this.pad.clear()
    } else {
      const ctx = this.canvas.getContext('2d')
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    }

    if (this.textInput) {
      this.textInput.value = ''
    }
  }

  async submit () {
    try {
      const blob = await cropCanvasAndExportToPNG(this.canvas)
      const file = new File([blob], 'signature.png', { type: 'image/png' })

      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)

      this.input.files = dataTransfer.files

      if (this.input.webkitEntries.length) {
        this.input.dataset.file = `${dataTransfer.files[0].name}`
      }

      this.closest('form').requestSubmit()
    } catch (error) {
      console.error('Failed to save signature:', error)
      this.button.disabled = false
      window.showAlert?.('Please type a signature before saving') || alert('Please type a signature before saving')
    }
  }
})
